import React from 'react';

import { Container } from '@/components/ui';
import { cn } from '@/utils/classname';

export interface SubscriptionTierSelectorProps {
  selectedTier: string;
  onTierChange: (tier: string) => void;
  disabled?: boolean;
  tiers?: string[];
  className?: string;
}

/**
 * SubscriptionTierSelector - Reusable component for subscription tier selection
 * Extracted from configure/index.tsx for use in both settings and wizard
 */
export const SubscriptionTierSelector: React.FC<
  SubscriptionTierSelectorProps
> = ({
  selectedTier,
  onTierChange,
  disabled = false,
  tiers = ['freemium', 'foundational', 'professional', 'ultimate'],
  className,
}) => {
  const tierDisplayNames = {
    freemium: 'Freemium',
    foundational: 'Foundational',
    professional: 'Professional',
    ultimate: 'Ultimate',
  } as const;

  return (
    <section
      className={cn('space-y-4', className)}
      aria-labelledby="tier-section-title"
    >
      <h3
        id="tier-section-title"
        className="text-chariot-text-primary text-lg font-semibold"
      >
        Subscription Tier
      </h3>
      <div aria-describedby="tier-description">
        <p id="tier-description" className="sr-only">
          Select your subscription tier to access different features and
          capabilities
        </p>
        <div
          className={cn(
            'grid gap-3',
            tiers.length === 4
              ? 'grid-cols-4'
              : `grid-cols-${Math.min(tiers.length, 3)}`
          )}
        >
          {tiers.map(tier => {
            const isSelected = selectedTier === tier;
            const displayName =
              tierDisplayNames[tier as keyof typeof tierDisplayNames] || tier;

            return (
              <Container
                key={tier}
                onClick={() => !disabled && onTierChange(tier)}
                className={cn(
                  'relative cursor-pointer py-6 text-center',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
                isSelected={isSelected}
                data-testid={`tier-button-${tier}`}
              >
                {isSelected && (
                  <div className="bg-chariot-brand-primary absolute -top-3 left-1/2 -translate-x-1/2 transform rounded-full px-3 py-1 text-xs font-bold text-white">
                    YOUR PLAN
                  </div>
                )}
                <span className="text-base font-medium">{displayName}</span>
              </Container>
            );
          })}
        </div>
      </div>
    </section>
  );
};
